import React, { useState } from "react";
import { HiOutlineLockClosed } from "react-icons/hi2";

/**
 * Shared physical sidebar shell for the flight flow: `fw-summary` (desktop) + mobile sheet.
 * The agent mockup and the public offer page both use this so wrappers/cards match.
 *
 * @param {object} props
 * @param {(o: { compact: boolean }) => React.ReactNode} props.renderStack - builds `fw-summary-stack` content (agent uses compact in mobile sheet)
 * @param {string} [props.ariaLabel]
 * @param {string} [props.variant] - e.g. `flight-booking` → `fw-summary--flight-booking` + כרטיס מאוחד
 * @param {string} [props.stackClassName] - מחלקות נוספות על `fw-summary-stack`
 * @param {string} [props.asideClassName] - מחלקות נוספות על `<aside>` (דסקטופ)
 * @param {string} [props.summaryCardClassName] - מחלקות נוספות על כרטיס הסיכום
 * @param {string} [props.mobileClassName] - מחלקות על עטיפת המובייל התחתונה
 * @param {React.ReactNode} [props.mobileBarDetail] - תוספת בשורת הסיכום הקבועה (למשל סכום)
 * @param {{ label: string, onClick: () => void, disabled?: boolean, busy?: boolean } | null} [props.mobilePayStrip]
 *        כשמוגדר — פס תחתון עם סכום + כפתור תשלום (פומבי), ובנוסף כפתור לפתיחת פירוט.
 */
function stripLockEmoji(label) {
  if (label == null) return "";
  return String(label)
    .replace(/\uFE0F/g, "")
    .replace(/🔒/gu, "")
    .trim()
    .replace(/\s{2,}/g, " ");
}

export default function FlightSummaryRail({
  renderStack,
  ariaLabel = "סיכום הזמנה",
  variant = "",
  stackClassName = "",
  asideClassName = "",
  summaryCardClassName = "",
  mobileClassName = "",
  mobileBarDetail = null,
  mobilePayStrip = null,
}) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const asideClass = [
    "fw-summary",
    "fw-summary--desktop",
    variant ? `fw-summary--${variant}` : "",
    asideClassName,
  ]
    .filter(Boolean)
    .join(" ");
  const cardClass = [
    "fw-summary-card",
    variant ? `fw-summary-card--${variant}` : "",
    summaryCardClassName,
  ]
    .filter(Boolean)
    .join(" ");
  const stackClass = ["fw-summary-stack", stackClassName].filter(Boolean).join(" ");
  const mobileSheetCardClass = [cardClass, "fw-summary-card--sheet"]
    .filter(Boolean)
    .join(" ");

  const mobileWrapClass = ["fw-summary-mobile", mobileClassName].filter(Boolean).join(" ");
  const mobileWrapExtra =
    mobilePayStrip != null ? `${mobileWrapClass} fw-summary-mobile--pay-strip`.trim() : mobileWrapClass;

  return (
    <>
      <aside className={asideClass} aria-label={ariaLabel}>
        <div className={cardClass}>
          <div className={stackClass}>{renderStack({ compact: false })}</div>
        </div>
      </aside>

      <div className={mobileWrapExtra}>
        {mobilePayStrip != null ? (
          <>
            <div className="fw-summary-mobile__pay-strip">
              {mobileBarDetail ? (
                <div className="fw-summary-mobile__pay-strip-sum" translate="no">
                  {mobileBarDetail}
                </div>
              ) : (
                <div className="fw-summary-mobile__pay-strip-sum fw-summary-mobile__pay-strip-sum--placeholder">
                  —
                </div>
              )}
              <button
                type="button"
                className="fw-summary-mobile__pay-strip-cta"
                disabled={mobilePayStrip.disabled}
                onClick={mobilePayStrip.onClick}
              >
                {mobilePayStrip.busy ? (
                  "טוען…"
                ) : (
                  <>
                    <span className="fw-summary-mobile__pay-strip-cta-label">
                      {stripLockEmoji(mobilePayStrip.label)}
                    </span>
                    <HiOutlineLockClosed
                      className="fw-summary-mobile__pay-strip-cta-lock"
                      aria-hidden
                    />
                  </>
                )}
              </button>
            </div>
            <button
              type="button"
              className="fw-summary-mobile__bar fw-summary-mobile__bar--sheet-toggle"
              aria-expanded={mobileOpen}
              onClick={() => setMobileOpen((o) => !o)}
            >
              <span className="fw-summary-mobile__bar-main">
                <span className="fw-summary-mobile__bar-label">פירוט סיכום והמשך</span>
              </span>
              <span className="fw-summary-mobile__bar-chevron" aria-hidden>
                {mobileOpen ? "▾" : "◂"}
              </span>
            </button>
          </>
        ) : (
          <button
            type="button"
            className="fw-summary-mobile__bar"
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((o) => !o)}
          >
            <span className="fw-summary-mobile__bar-main">
              <span className="fw-summary-mobile__bar-label">סיכום הזמנה</span>
              {mobileBarDetail ? (
                <span className="fw-summary-mobile__bar-detail">{mobileBarDetail}</span>
              ) : null}
            </span>
            <span className="fw-summary-mobile__bar-chevron" aria-hidden>
              {mobileOpen ? "▾" : "◂"}
            </span>
          </button>
        )}
        {mobileOpen ? (
          <div className="fw-summary-mobile__sheet" role="region">
            <div className={mobileSheetCardClass}>
              <div className={stackClass}>{renderStack({ compact: true })}</div>
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
}
